import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth, api, apiClient } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  LayoutDashboard, FileText, Settings, RefreshCw, LogOut, Users, 
  Clock, AlertCircle, CheckCircle, UtensilsCrossed, Calendar,
  ChevronRight, Loader2, Download, Plus, Trash2, Save,
  AlertTriangle, ArrowLeft, ChevronDown, ChevronUp, Printer, MapPin, Bus, X, ShieldAlert, Pencil, ArrowLeftRight
} from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_lunch-roster-hub/artifacts/wio7p25o_rr%20logo.png';

const mergeGroupDisplay = (groups) => {
  const nonCit = groups.filter(g => !g.toUpperCase().startsWith('CIT'));
  const hasCit = groups.some(g => g.toUpperCase().startsWith('CIT'));
  return hasCit ? [...nonCit, 'CIT'] : nonCit;
};

// Admin Layout Component
export const AdminLayout = () => {
  const { logoutAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logoutAdmin();
    navigate('/');
  };

  const navItems = [
    { path: '/admin/dashboard', label: 'Kitchen View', icon: LayoutDashboard },
    { path: '/admin/reports', label: 'Reports', icon: FileText },
    { path: '/admin/trips', label: 'Trips', icon: MapPin },
    { path: '/admin/dietary', label: 'Dietary', icon: ShieldAlert },
    { path: '/admin/settings', label: 'Settings', icon: Settings },
    { path: '/admin/sync', label: 'Sync', icon: RefreshCw },
  ];

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Top Header */}
      <header className="bg-slate-900 text-white p-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Rolling River" className="w-10 h-10" />
            <div>
              <h1 className="font-bold text-lg">Rolling River Kitchen</h1>
              <p className="text-xs text-slate-400">Admin Panel</p>
            </div>
          </div>
          
          <nav className="hidden md:flex items-center gap-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors
                           ${location.pathname === item.path 
                             ? 'bg-[#2B4F8C] text-white' 
                             : 'text-slate-300 hover:bg-slate-800'}`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href="https://campersnapshot.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-white px-2 py-1 rounded-lg hover:bg-slate-800 transition-colors text-xs font-medium"
              data-testid="campersnapshot-link"
            >
              Back to CamperSnapshot
            </a>
            <Button variant="ghost" onClick={handleLogout} className="text-white hover:bg-slate-800">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      <nav className="md:hidden flex overflow-x-auto bg-slate-800 px-2 py-2 gap-2">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap text-sm
                       ${location.pathname === item.path 
                         ? 'bg-[#2B4F8C] text-white' 
                         : 'text-slate-300 hover:bg-slate-700'}`}
          >
            <item.icon className="w-4 h-4" />
            {item.label}
          </Link>
        ))}
      </nav>

      <main className="max-w-7xl mx-auto">
        <Outlet />
      </main>
    </div>
  );
};

// Kitchen Dashboard - Groups by Lunch Period
export const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [config, setConfig] = useState(null);
  const [groups, setGroups] = useState([]);
  const [expandedPeriod, setExpandedPeriod] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showFoodModal, setShowFoodModal] = useState(false);
  const [foodName, setFoodName] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [reportRes, configRes, groupsRes] = await Promise.all([
        api.getDailyReport(),
        api.getConfig(),
        api.getGroups()
      ]);
      setReport(reportRes.data);
      setConfig(configRes.data);
      setGroups(groupsRes.data);
      
      // Auto-expand first period
      if (configRes.data.config?.lunch_periods?.length > 0) {
        setExpandedPeriod(configRes.data.config.lunch_periods[0].period_number);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleSetFoodOfDay = async () => {
    if (!foodName.trim()) {
      toast.error('Please enter a food name');
      return;
    }
    try {
      await api.setFoodOfDay({ food_name: foodName.trim() });
      toast.success('Food of the day updated!');
      setShowFoodModal(false);
      setFoodName('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const getGroupStatus = (group) => {
    const order = report?.orders?.find(o => 
      o.group_name?.toLowerCase() === group.name?.toLowerCase() || o.group_id === group.id
    );
    const totalCampers = group?.camper_count || 0;
    const orderedCount = order?.orders?.length || 0;
    
    if (orderedCount === 0) return { status: 'pending', ordered: 0, total: totalCampers };
    if (orderedCount >= totalCampers) return { status: 'complete', ordered: orderedCount, total: totalCampers };
    return { status: 'partial', ordered: orderedCount, total: totalCampers };
  };

  const getGroupOrder = (group) => {
    return report?.orders?.find(o => 
      o.group_name?.toLowerCase() === group.name?.toLowerCase() || o.group_id === group.id
    );
  };

  const getGroupsForPeriod = (periodGroups) => {
    if (!periodGroups || periodGroups.length === 0) {
      return [];  // Don't show any groups if none assigned
    }
    return groups.filter(g => {
      // Check if group matches any of the period group names
      return periodGroups.some(pg => {
        const pgLower = pg.toLowerCase();
        const gName = g.name?.toLowerCase() || '';
        const gId = g.id?.toLowerCase() || '';
        // Match exact, partial, or contains
        return gName === pgLower || 
               gId === pgLower || 
               gName.includes(pgLower) || 
               pgLower.includes(gName.split(' ')[0]); // Match "B05" in "B05 Crocodiles"
      });
    });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#2B4F8C]" />
      </div>
    );
  }

  const lunchPeriods = config?.config?.lunch_periods || [
    { period_number: 1, name: 'Lunch Period 1', time: '11:30 AM EST', groups: [] },
    { period_number: 2, name: 'Lunch Period 2', time: '12:00 PM EST', groups: [] },
    { period_number: 3, name: 'Lunch Period 3', time: '12:30 PM EST', groups: [] }
  ];

  const completedGroups = report?.orders?.length || 0;
  const totalGroups = groups.length;

  return (
    <div className="p-4 lg:p-6">
      {/* Header Stats */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-900">Welcome Cristina</h1>
          <p className="text-slate-600">{report?.date} • {config?.current_time_est}</p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-sm text-slate-600">Groups Complete</p>
            <p className="text-2xl font-bold">
              <span className="text-green-600">{completedGroups}</span>
              <span className="text-slate-400"> / {totalGroups}</span>
            </p>
          </div>
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Food of Day Banner */}
      <Card className="mb-6 border-2 border-amber-200 bg-amber-50">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <UtensilsCrossed className="w-8 h-8 text-amber-600" />
            <div>
              <p className="text-sm font-semibold text-amber-800">TODAY'S SPECIAL</p>
              <p className="text-xl font-bold text-amber-900">
                {config?.config?.food_of_day || 'Not Set'}
              </p>
            </div>
          </div>
          <Button onClick={() => setShowFoodModal(true)} variant="outline" data-testid="set-food-btn">
            {config?.config?.food_of_day ? 'Change' : 'Set Food'}
          </Button>
        </CardContent>
      </Card>

      {/* Cutoff Status */}
      {config?.cutoff_active ? (
        <div className="bg-red-100 border border-red-200 rounded-lg p-3 mb-6 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-800 font-medium">Orders closed (Cutoff: 9:30 AM EST)</span>
        </div>
      ) : !config?.cutoff_enabled ? (
        <div className="bg-amber-100 border border-amber-200 rounded-lg p-3 mb-6 flex items-center gap-2">
          <Clock className="w-5 h-5 text-amber-600" />
          <span className="text-amber-800 font-medium">Order deadline is OFF — orders accepted anytime</span>
        </div>
      ) : (
        <div className="bg-green-100 border border-green-200 rounded-lg p-3 mb-6 flex items-center gap-2">
          <Clock className="w-5 h-5 text-green-600" />
          <span className="text-green-800 font-medium">Orders open until 9:30 AM EST</span>
        </div>
      )}

      {/* Selected Group Detail View */}
      {selectedGroup && (
        <Card className="mb-6 border-2 border-[#2B4F8C]">
          <CardHeader className="bg-[#2B4F8C] text-white">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                {selectedGroup.name}
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setSelectedGroup(null)}
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {(() => {
              const order = getGroupOrder(selectedGroup);
              if (!order) {
                return (
                  <div className="text-center py-8 text-slate-500">
                    <AlertCircle className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>No orders submitted yet</p>
                  </div>
                );
              }
              return (
                <div>
                  <div className="mb-4 flex items-center justify-between">
                    <Badge className="bg-green-100 text-green-800">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Submitted at {order.submitted_at?.split('T')[1]?.split(' ')[0]} EST
                    </Badge>
                    <span className="text-sm text-slate-500">{order.orders?.length} campers</span>
                  </div>
                  {/* Counselor & CIT Lunch of the Day */}
                  {order.counselor_cit_count > 0 && (
                    <div className="flex items-center justify-between p-3 bg-[#2B4F8C]/10 rounded-lg mb-3 border border-[#2B4F8C]/20">
                      <span className="font-bold text-[#2B4F8C]">Counselor & CIT — Lunch of the Day</span>
                      <Badge className="bg-[#2B4F8C] text-white text-base px-3">{order.counselor_cit_count}</Badge>
                    </div>
                  )}
                  <div className="space-y-2">
                    {order.orders?.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <span className="font-medium">{item.camper_name}</span>
                          <div className={`text-xs ${item.allergies?.length > 0 && !item.allergies.every(a => ['no','none','n/a','na','ni'].includes(a.toLowerCase().trim()) || a.toLowerCase().startsWith('no ')) ? 'text-red-600 font-semibold' : 'text-slate-400'}`}>
                            Allergies: {(() => {
                              const noise = ['no','none','n/a','na','ni'];
                              const real = (item.allergies || []).filter(a => !noise.includes(a.toLowerCase().trim()) && !a.toLowerCase().startsWith('no '));
                              return real.length > 0 ? real.join(', ') : 'None';
                            })()}
                          </div>
                          {item.allergy_override_by && (
                            <div className="text-xs text-amber-700 font-semibold" data-testid={`override-by-${item.camper_id}`}>
                              Allergy override by {item.allergy_override_by}
                            </div>
                          )}
                        </div>
                        <Badge variant="secondary">{item.lunch_choice}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Lunch Periods */}
      {!selectedGroup && (
        <div className="space-y-4">
          {lunchPeriods.map((period) => {
            const periodGroups = getGroupsForPeriod(period.groups);
            const completedInPeriod = periodGroups.filter(g => getGroupStatus(g).status === 'complete').length;
            const partialInPeriod = periodGroups.filter(g => getGroupStatus(g).status === 'partial').length;
            const totalOrdered = periodGroups.reduce((sum, g) => sum + getGroupStatus(g).ordered, 0);
            const totalCampers = periodGroups.reduce((sum, g) => sum + getGroupStatus(g).total, 0);
            const isExpanded = expandedPeriod === period.period_number;
            
            return (
              <Card key={period.period_number} className="border-2">
                {/* Period Header */}
                <button
                  onClick={() => setExpandedPeriod(isExpanded ? null : period.period_number)}
                  className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#2B4F8C] text-white flex items-center justify-center font-bold text-lg">
                      {period.period_number}
                    </div>
                    <div className="text-left">
                      <h3 className="font-bold text-lg text-slate-900">{period.name}</h3>
                      <p className="text-sm text-slate-500">{period.time}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Ordered</p>
                      <p className="font-bold">
                        <span className={totalOrdered === totalCampers ? 'text-green-600' : totalOrdered > 0 ? 'text-amber-600' : 'text-slate-400'}>
                          {totalOrdered}
                        </span>
                        <span className="text-slate-400"> / {totalCampers} campers</span>
                      </p>
                      <p className="text-xs text-slate-400">
                        {completedInPeriod} of {periodGroups.length} groups done
                        {partialInPeriod > 0 && ` · ${partialInPeriod} in progress`}
                      </p>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-6 h-6 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-6 h-6 text-slate-400" />
                    )}
                  </div>
                </button>

                {/* Period Groups */}
                {isExpanded && (
                  <div className="border-t p-4 bg-slate-50">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {periodGroups.map((group) => {
                        const { status, ordered, total } = getGroupStatus(group);
                        const remaining = total - ordered;
                        const order = getGroupOrder(group);
                        
                        return (
                          <button
                            key={group.id}
                            onClick={() => setSelectedGroup(group)}
                            className={`p-4 rounded-xl border-2 text-left transition-all hover:shadow-md ${
                              status === 'complete'
                                ? 'bg-green-50 border-green-300 hover:border-green-400'
                                : status === 'partial'
                                ? 'bg-amber-50 border-amber-300 hover:border-amber-400'
                                : 'bg-white border-slate-200 hover:border-[#2B4F8C]'
                            }`}
                            data-testid={`group-card-${group.id}`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-bold text-slate-900 text-sm">{group.name}</h4>
                              {status === 'complete' ? (
                                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                              ) : status === 'partial' ? (
                                <Clock className="w-5 h-5 text-amber-500 flex-shrink-0" />
                              ) : (
                                <Clock className="w-5 h-5 text-slate-300 flex-shrink-0" />
                              )}
                            </div>
                            <p className="text-sm font-semibold">
                              <span className={status === 'complete' ? 'text-green-700' : status === 'partial' ? 'text-amber-700' : 'text-slate-400'}>
                                {ordered} of {total} ordered
                              </span>
                            </p>
                            {remaining > 0 && ordered > 0 && (
                              <p className="text-xs text-red-500 mt-1">{remaining} still need to order</p>
                            )}
                            {ordered === 0 && (
                              <p className="text-xs text-slate-400 mt-1">No orders yet</p>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Food of Day Modal */}
      <Dialog open={showFoodModal} onOpenChange={setShowFoodModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Today's Special</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Food Name</Label>
              <Input
                value={foodName}
                onChange={(e) => setFoodName(e.target.value)}
                placeholder="e.g., Grilled Chicken, Pizza, Hot Dogs"
                data-testid="food-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFoodModal(false)}>Cancel</Button>
            <Button onClick={handleSetFoodOfDay} data-testid="save-food-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Reports Page - By Day, Lunch Period, Group
export const AdminReports = () => {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [config, setConfig] = useState(null);
  const [selectedFood, setSelectedFood] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const [reportRes, configRes] = await Promise.all([
        api.getDailyReport(selectedDate),
        api.getConfig()
      ]);
      setReport(reportRes.data);
      setConfig(configRes.data);
    } catch (error) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    setSelectedFood(null);
    fetchReport();
  }, [fetchReport]);

  const handleExportCSV = async () => {
    try {
      const response = await api.exportReportCSV(selectedDate);
      const blob = new Blob([response.data.csv_content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      toast.success('Report exported');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const handlePrintPeriod = async (period) => {
    try {
      toast.info(`Generating PDF for ${period.name}...`);
      const response = await api.downloadReportPDF(selectedDate, period.period_number);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `RRDC_${period.name.replace(/ /g, '_')}_${selectedDate}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  const handlePrintAll = () => {
    handleDownloadPDF();
  };

  const handleDownloadPDF = async () => {
    try {
      toast.info('Generating PDF...');
      const response = await api.downloadReportPDF(selectedDate);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const dt = new Date(selectedDate + 'T12:00:00');
      const nice = dt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).replace(/ /g, '_');
      a.download = `RRDC_Lunch_Report_${nice}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  const filterAllergies = (allergies) => {
    const noise = ['no', 'none', 'n/a', 'na', 'ni'];
    return (allergies || []).filter(a => !noise.includes(a.toLowerCase().trim()) && !a.toLowerCase().startsWith('no '));
  };

  const renderPeriodBlock = (period, periodOrders, periodFoodTotals) => {
    const totalCampers = periodOrders.reduce((sum, o) => sum + (o.orders?.length || 0), 0);
    return (
      <div key={period.period_number} className="print-period-section mb-10">
        <div className="text-center mb-6 border-b-2 border-[#2B4F8C] pb-4">
          <h2 className="text-2xl font-bold text-[#2B4F8C]">{period.name} — {period.time}</h2>
        </div>

        {Object.keys(periodFoodTotals).length > 0 && (
          <div className="mb-6 p-4 border-2 border-[#2B4F8C] rounded-xl bg-blue-50">
            <h3 className="text-lg font-black text-[#2B4F8C] mb-3">
              FOOD TOTALS — {totalCampers} Campers
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(periodFoodTotals)
                .sort((a, b) => b[1] - a[1])
                .map(([food, count]) => (
                  <div key={food} className="flex justify-between items-center bg-white p-2 rounded-lg border border-slate-200">
                    <span className="font-semibold">{food}</span>
                    <span className="font-black text-lg text-[#2B4F8C]">×{count}</span>
                  </div>
                ))}
            </div>
          </div>
        )}

        <div className="space-y-4">
          {periodOrders.map((order) => (
            <div key={order.id || order.group_name} className="border-2 border-slate-300 rounded-xl overflow-hidden">
              <div className="bg-[#2B4F8C] text-white px-4 py-2">
                <h4 className="text-lg font-bold">
                  {order.group_name} — {order.orders?.length} campers
                </h4>
              </div>
              {/* Group Food Totals */}
              {order.group_food_totals && Object.keys(order.group_food_totals).length > 0 && (
                <div className="px-4 py-2 bg-blue-50 border-b flex flex-wrap gap-2">
                  {Object.entries(order.group_food_totals)
                    .sort((a, b) => b[1] - a[1])
                    .map(([food, count]) => (
                      <span key={food} className="text-sm font-medium text-[#2B4F8C] bg-white px-2 py-1 rounded border border-blue-200">
                        {food}: <strong>{count}</strong>
                      </span>
                    ))}
                </div>
              )}
              <table className="w-full">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left py-2 px-4 font-bold text-slate-700 w-1/2">Camper Name</th>
                    <th className="text-left py-2 px-4 font-bold text-slate-700">Lunch Choice</th>
                  </tr>
                </thead>
                <tbody>
                  {order.orders?.map((item, idx) => {
                    const realAllergies = filterAllergies(item.allergies);
                    return (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        <td className="py-2 px-4">
                          <div className="text-base font-medium">{item.camper_name}</div>
                          <div className={`text-sm ${realAllergies.length > 0 ? 'text-red-600 font-semibold' : 'text-slate-400'}`}>
                            Allergies: {realAllergies.length > 0 ? realAllergies.join(', ') : 'None'}
                          </div>
                          {item.allergy_override_by && (
                            <div className="text-xs text-amber-700 font-semibold" data-testid={`report-override-by-${item.camper_id}`}>
                              Allergy override by {item.allergy_override_by}
                            </div>
                          )}
                        </td>
                        <td className="py-2 px-4 text-base font-medium">{item.lunch_choice}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>

        {periodOrders.length === 0 && (
          <p className="text-center text-slate-400 py-6">No orders for this period</p>
        )}
      </div>
    );
  };

  const getOrdersForPeriod = (period) => {
    if (!period.groups || period.groups.length === 0) return [];
    return report?.orders?.filter(o => 
      period.groups.some(pg => {
        const pgLower = pg.toLowerCase();
        const oName = o.group_name?.toLowerCase() || '';
        return oName === pgLower || oName.includes(pgLower) || pgLower.includes(oName.split(' ')[0]);
      })
    ) || [];
  };

  const getFoodTotalsForPeriod = (orders) => {
    const totals = {};
    orders.forEach(order => {
      order.orders?.forEach(item => {
        const choice = item.lunch_choice;
        totals[choice] = (totals[choice] || 0) + 1;
      });
    });
    return totals;
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#2B4F8C]" />
      </div>
    );
  }

  const lunchPeriods = config?.config?.lunch_periods || [];


  return (
    <div className="p-4 lg:p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6 no-print">
        <div>
          <h1 className="text-2xl font-black text-slate-900">Daily Report</h1>
          <p className="text-slate-600">Breakdown by Day → Lunch Period → Group</p>
        </div>
        <div className="flex items-center gap-3">
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-auto"
          />
          <Button onClick={handlePrintAll} data-testid="print-all-btn" className="bg-[#2B4F8C] hover:bg-[#1E3A6B] text-white">
            <Printer className="w-4 h-4 mr-2" />
            Print All Periods
          </Button>
          <Button onClick={handleDownloadPDF} data-testid="download-pdf-btn" className="bg-green-700 hover:bg-green-800 text-white">
            <Download className="w-4 h-4 mr-2" />
            Download PDF
          </Button>
          <Button onClick={handleExportCSV} variant="outline" data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Date Header */}
      <Card className="mb-6 bg-[#2B4F8C] text-white no-print">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-200 text-sm">Report Date</p>
              <h2 className="text-3xl font-bold">{selectedDate}</h2>
            </div>
            <div className="text-right">
              <p className="text-4xl font-bold">{report?.total_campers || 0}</p>
              <p className="text-blue-200">Total Orders</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Food Totals */}
      <Card className="mb-6 no-print">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Food Totals</CardTitle>
            {selectedFood && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setSelectedFood(null)}
                data-testid="clear-food-filter-btn"
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                <X className="w-4 h-4 mr-1" /> Clear Filter
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {report?.food_totals && Object.keys(report.food_totals).length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(report.food_totals)
                .sort((a, b) => b[1] - a[1])
                .map(([food, count]) => (
                  <div 
                    key={food} 
                    className={`p-4 rounded-xl text-center cursor-pointer transition-all border-2 ${
                      selectedFood === food 
                        ? 'bg-[#2B4F8C] text-white border-[#2B4F8C] ring-2 ring-[#2B4F8C] ring-offset-2' 
                        : 'bg-slate-50 border-transparent hover:border-[#2B4F8C] hover:bg-blue-50'
                    }`}
                    onClick={() => setSelectedFood(selectedFood === food ? null : food)}
                    data-testid={`food-filter-${food.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <p className={`text-3xl font-bold ${selectedFood === food ? 'text-white' : 'text-[#2B4F8C]'}`}>{count}</p>
                    <p className={`text-sm mt-1 ${selectedFood === food ? 'text-blue-100' : 'text-slate-600'}`}>{food}</p>
                  </div>
                ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-4">No orders for this date</p>
          )}

          {/* Filtered camper list when a food is selected */}
          {selectedFood && report?.orders && (
            <div className="mt-4 border-t pt-4">
              <h3 className="font-bold text-lg text-[#2B4F8C] mb-3">
                Showing: {selectedFood} — {report.food_totals?.[selectedFood] || 0} campers
              </h3>
              <div className="divide-y">
                {report.orders
                  .filter(order => order.orders?.some(item => item.lunch_choice === selectedFood))
                  .map(order => {
                    const matchingCampers = order.orders.filter(item => item.lunch_choice === selectedFood);
                    return (
                      <div key={order.id || order.group_id} className="py-2">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-sm text-slate-700">{order.group_name}</span>
                          <Badge variant="outline" className="text-xs">{matchingCampers.length}</Badge>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {matchingCampers.map((item, idx) => (
                            <span key={idx} className="text-sm bg-blue-50 text-[#2B4F8C] px-2 py-0.5 rounded-md">
                              {item.camper_name}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* By Lunch Period */}
      {lunchPeriods.map((period) => {
        const periodOrders = getOrdersForPeriod(period);
        const periodFoodTotals = getFoodTotalsForPeriod(periodOrders);
        const totalCampers = periodOrders.reduce((sum, o) => sum + (o.orders?.length || 0), 0);

        return (
          <Card key={period.period_number} className="mb-6">
            <CardHeader className="bg-slate-100">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-3">
                  <span>{period.name} - {period.time}</span>
                  <Badge variant="secondary">{periodOrders.length} groups • {totalCampers} campers</Badge>
                </CardTitle>
                <Button 
                  onClick={() => handlePrintPeriod(period)}
                  variant="outline"
                  size="sm"
                  className="no-print"
                  data-testid={`print-period-${period.period_number}-btn`}
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Period
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              {/* Period Food Totals */}
              {Object.keys(periodFoodTotals).length > 0 && (
                <div className="mb-4 p-3 bg-amber-50 rounded-lg">
                  <p className="text-sm font-semibold text-amber-800 mb-2">Period Totals:</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(periodFoodTotals)
                      .sort((a, b) => b[1] - a[1])
                      .map(([food, count]) => (
                        <Badge key={food} variant="secondary" className="bg-white">
                          {food}: <span className="font-bold ml-1">{count}</span>
                        </Badge>
                      ))}
                  </div>
                </div>
              )}
              
              {/* Groups */}
              {periodOrders.length > 0 ? (
                <div className="divide-y">
                  {periodOrders.map((order) => (
                    <div key={order.id} className="py-3">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-bold">{order.group_name}</h4>
                        <Badge className="bg-green-100 text-green-800">
                          {order.orders?.length} orders
                        </Badge>
                      </div>
                      {/* Group Food Totals */}
                      {order.group_food_totals && Object.keys(order.group_food_totals).length > 0 && (
                        <div className="mb-2 flex flex-wrap gap-1">
                          {Object.entries(order.group_food_totals)
                            .sort((a, b) => b[1] - a[1])
                            .map(([food, count]) => (
                              <Badge key={food} variant="outline" className="text-xs bg-blue-50 text-[#2B4F8C] border-blue-200">
                                {food}: {count}
                              </Badge>
                            ))}
                        </div>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {order.orders?.map((item, idx) => (
                          <div key={idx} className="p-2 bg-slate-50 rounded text-sm">
                            <div className="flex items-center justify-between">
                              <span className="font-medium">{item.camper_name}</span>
                              <span className="text-slate-600 font-medium">{item.lunch_choice}</span>
                            </div>
                            <div className={`text-xs ${item.allergies?.length > 0 && !item.allergies.every(a => ['no','none','n/a','na','ni'].includes(a.toLowerCase().trim()) || a.toLowerCase().startsWith('no ')) ? 'text-red-600 font-semibold' : 'text-slate-400'}`}>
                              Allergies: {(() => {
                                const noise = ['no','none','n/a','na','ni'];
                                const real = (item.allergies || []).filter(a => !noise.includes(a.toLowerCase().trim()) && !a.toLowerCase().startsWith('no '));
                                return real.length > 0 ? real.join(', ') : 'None';
                              })()}
                            </div>
                            {item.allergy_override_by && (
                              <div className="text-xs text-amber-700 font-semibold">
                                Allergy override by {item.allergy_override_by}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">No orders for this period</p>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Unassigned Groups — orders from groups not in any lunch period */}
      {(() => {
        const allPeriodGroups = lunchPeriods.flatMap(p => p.groups || []).map(g => g.toLowerCase());
        const unassignedOrders = (report?.orders || []).filter(o => {
          const oName = (o.group_name || '').toLowerCase();
          return !allPeriodGroups.some(pg => oName === pg || oName.includes(pg) || pg.includes(oName.split(' ')[0]));
        });
        if (unassignedOrders.length === 0) return null;
        const unassignedTotals = getFoodTotalsForPeriod(unassignedOrders);
        const totalCampers = unassignedOrders.reduce((sum, o) => sum + (o.orders?.length || 0), 0);

        return (
          <Card className="mb-6 border-amber-300">
            <CardHeader className="bg-amber-50">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-3">
                  <span>Other Groups (Not Assigned to a Period)</span>
                  <Badge variant="secondary">{unassignedOrders.length} groups &bull; {totalCampers} campers</Badge>
                </CardTitle>
                <Button 
                  onClick={() => handlePrintPeriod({ name: 'Other Groups', time: 'Unassigned', groups: unassignedOrders.map(o => o.group_name), period_number: 'other' })}
                  variant="outline"
                  size="sm"
                  className="no-print"
                  data-testid="print-period-other-btn"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              {Object.keys(unassignedTotals).length > 0 && (
                <div className="mb-4 p-3 bg-amber-50 rounded-lg">
                  <p className="text-sm font-semibold text-amber-800 mb-2">Totals:</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(unassignedTotals)
                      .sort((a, b) => b[1] - a[1])
                      .map(([food, count]) => (
                        <Badge key={food} variant="secondary" className="bg-white">
                          {food}: <span className="font-bold ml-1">{count}</span>
                        </Badge>
                      ))}
                  </div>
                </div>
              )}
              <div className="divide-y">
                {unassignedOrders.map((order) => (
                  <div key={order.id || order.group_name} className="py-3">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-bold">{order.group_name}</h4>
                      <Badge className="bg-green-100 text-green-800">
                        {order.orders?.length} orders
                      </Badge>
                    </div>
                    {order.group_food_totals && Object.keys(order.group_food_totals).length > 0 && (
                      <div className="mb-2 flex flex-wrap gap-1">
                        {Object.entries(order.group_food_totals)
                          .sort((a, b) => b[1] - a[1])
                          .map(([food, count]) => (
                            <Badge key={food} variant="outline" className="text-xs bg-blue-50 text-[#2B4F8C] border-blue-200">
                              {food}: {count}
                            </Badge>
                          ))}
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {order.orders?.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                          <span>{item.camper_name}</span>
                          <span className="text-slate-600 font-medium">{item.lunch_choice}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Staff Orders Section removed */}
    </div>
  );
};

// Settings Page
export const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [alternatives, setAlternatives] = useState([]);
  const [lunchPeriods, setLunchPeriods] = useState([]);
  const [staffMenuItems, setStaffMenuItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [menuSchedule, setMenuSchedule] = useState({});
  const [showSchedule, setShowSchedule] = useState(false);
  const [uploadingSchedule, setUploadingSchedule] = useState(false);
  const [editingDate, setEditingDate] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [swapA, setSwapA] = useState('');
  const [swapB, setSwapB] = useState('');
  const [savingSchedule, setSavingSchedule] = useState(false);

  const saveSchedule = async (newSchedule, successMsg) => {
    setSavingSchedule(true);
    try {
      await api.updateMenuSchedule(newSchedule);
      setMenuSchedule(newSchedule);
      toast.success(successMsg);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save schedule');
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleSaveEdit = async (date) => {
    const value = editValue.trim();
    if (!value) { toast.error('Enter a menu item name'); return; }
    await saveSchedule({ ...menuSchedule, [date]: value }, `${date}: Lunch of the Day set to "${value}"`);
    setEditingDate(null);
    setEditValue('');
  };

  const handleSwapDays = async () => {
    if (!swapA || !swapB) { toast.error('Pick both days to swap'); return; }
    if (swapA === swapB) { toast.error('Pick two different days'); return; }
    const next = { ...menuSchedule, [swapA]: menuSchedule[swapB], [swapB]: menuSchedule[swapA] };
    await saveSchedule(next, `Swapped: ${swapA} ↔ ${swapB}`);
    setSwapA('');
    setSwapB('');
  };

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, groupsRes, scheduleRes] = await Promise.all([
        api.getConfig(),
        api.getGroups(),
        api.getMenuSchedule()
      ]);
      setConfig(configRes.data);
      setAlternatives(configRes.data.config?.alternatives || []);
      setLunchPeriods(configRes.data.config?.lunch_periods || []);
      setStaffMenuItems(configRes.data.config?.staff_menu_items || []);
      setGroups(groupsRes.data);
      setMenuSchedule(scheduleRes.data?.schedule || {});
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleAddAlternative = () => {
    const newId = Date.now().toString();
    setAlternatives([...alternatives, { id: newId, name: '', is_active: true }]);
    // Scroll to the new item after render
    setTimeout(() => {
      const el = document.getElementById(`menu-item-${newId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-[#2B4F8C]', 'ring-offset-2');
        setTimeout(() => el.classList.remove('ring-2', 'ring-[#2B4F8C]', 'ring-offset-2'), 2000);
      }
    }, 100);
    toast.success('New item added — fill in the name below');
  };

  const handleSaveAlternatives = async () => {
    const valid = alternatives.filter(a => a.name.trim());
    setSaving(true);
    try {
      await api.updateAlternatives(valid);
      toast.success('Menu saved!');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePeriods = async () => {
    setSaving(true);
    try {
      await api.updateLunchPeriods(lunchPeriods);
      toast.success('Lunch periods saved!');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#2B4F8C]" />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-black text-slate-900">Settings</h1>
        <Button 
          variant="outline"
          onClick={() => {
            const a = document.createElement('a');
            a.href = `${process.env.REACT_APP_BACKEND_URL}/api/admin/sop-pdf`;
            a.download = 'Rolling-River-Lunch-Ordering-Admin-Guide.pdf';
            a.click();
          }}
          data-testid="download-sop-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Download Admin Guide (PDF)
        </Button>
      </div>

      {/* Order Deadline Toggle */}
      <Card className="border-2 border-[#2B4F8C]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Order Deadline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <p className="font-semibold text-lg">9:30 AM EST Daily Cutoff</p>
              <p className="text-sm text-slate-500">
                {config?.cutoff_enabled !== false
                  ? 'Orders will be locked at 9:30 AM EST each morning'
                  : 'Deadline is OFF — counselors can order anytime'}
              </p>
              {config?.cutoff_passed && config?.cutoff_enabled !== false && (
                <p className="text-sm text-red-600 font-medium mt-1">Currently past cutoff time</p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-sm font-medium ${config?.cutoff_enabled !== false ? 'text-green-700' : 'text-slate-400'}`}>
                {config?.cutoff_enabled !== false ? 'ON' : 'OFF'}
              </span>
              <button
                onClick={async () => {
                  const newState = config?.cutoff_enabled === false ? true : false;
                  try {
                    await api.toggleCutoff(newState);
                    setConfig(prev => ({ ...prev, cutoff_enabled: newState, cutoff_active: newState && prev?.cutoff_passed }));
                    toast.success(newState ? 'Order deadline enabled (9:30 AM EST)' : 'Order deadline disabled — orders open anytime');
                  } catch (error) {
                    toast.error('Failed to toggle deadline');
                  }
                }}
                data-testid="cutoff-toggle-btn"
                className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#2B4F8C] focus:ring-offset-2 ${
                  config?.cutoff_enabled !== false ? 'bg-[#2B4F8C]' : 'bg-slate-300'
                }`}
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-md transition-transform ${
                    config?.cutoff_enabled !== false ? 'translate-x-7' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Daily Menu Schedule */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Daily Menu Schedule
              </CardTitle>
              <p className="text-sm text-slate-500 mt-1">
                {Object.keys(menuSchedule).length} camp days scheduled (June 29 - August 20)
              </p>
            </div>
            <div className="flex items-center gap-2">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  data-testid="upload-schedule-input"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    setUploadingSchedule(true);
                    try {
                      const formData = new FormData();
                      formData.append('file', file);
                      const res = await apiClient.post('/api/admin/menu-schedule/upload', formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                      });
                      const d = res.data;
                      toast.success(`Uploaded ${d.total_days} camp days`);
                      if (d.errors?.length > 0) {
                        toast.warning(`${d.errors.length} rows had issues`);
                      }
                      fetchConfig();
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Upload failed');
                    } finally {
                      setUploadingSchedule(false);
                      e.target.value = '';
                    }
                  }}
                />
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-slate-200 text-sm font-medium hover:bg-slate-50 transition-colors">
                  {uploadingSchedule ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Upload Schedule
                </span>
              </label>
              <Button
                variant="outline"
                onClick={() => setShowSchedule(!showSchedule)}
                data-testid="toggle-schedule-btn"
              >
                {showSchedule ? <ChevronUp className="w-4 h-4 mr-2" /> : <ChevronDown className="w-4 h-4 mr-2" />}
                {showSchedule ? 'Hide' : 'View Schedule'}
              </Button>
            </div>
          </div>
        </CardHeader>
        {showSchedule && (
          <CardContent>
            {/* Swap two days */}
            <div className="mb-4 p-3 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 flex flex-wrap items-center gap-2" data-testid="swap-days-section">
              <ArrowLeftRight className="w-4 h-4 text-[#2B4F8C]" />
              <span className="text-sm font-semibold text-slate-700">Swap two days:</span>
              {[{ val: swapA, set: setSwapA, tid: 'swap-day-a' }, { val: swapB, set: setSwapB, tid: 'swap-day-b' }].map((sel, i) => (
                <select
                  key={i}
                  value={sel.val}
                  onChange={(e) => sel.set(e.target.value)}
                  className="h-9 rounded-md border border-slate-300 px-2 text-sm bg-white max-w-[280px]"
                  data-testid={sel.tid}
                >
                  <option value="">Select day...</option>
                  {Object.entries(menuSchedule).sort(([a], [b]) => a.localeCompare(b)).map(([date, food]) => {
                    const d = new Date(date + 'T12:00:00');
                    return (
                      <option key={date} value={date}>
                        {d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} — {food}
                      </option>
                    );
                  })}
                </select>
              ))}
              <Button size="sm" onClick={handleSwapDays} disabled={savingSchedule} className="bg-[#2B4F8C]" data-testid="swap-days-btn">
                {savingSchedule ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowLeftRight className="w-4 h-4 mr-1" />}
                Swap
              </Button>
            </div>

            <div className="rounded-lg border overflow-hidden">
              <table className="w-full text-sm" data-testid="menu-schedule-table">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left p-3 font-semibold">Date</th>
                    <th className="text-left p-3 font-semibold">Day</th>
                    <th className="text-left p-3 font-semibold">Lunch of the Day</th>
                    <th className="p-3 w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(menuSchedule)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([date, food]) => {
                      const d = new Date(date + 'T12:00:00');
                      const dayName = d.toLocaleDateString('en-US', { weekday: 'short' });
                      const displayDate = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      const isToday = date === new Date().toISOString().split('T')[0];
                      const isEditing = editingDate === date;
                      return (
                        <tr key={date} className={`border-t ${isToday ? 'bg-yellow-50 font-semibold' : ''}`}>
                          <td className="p-3">{displayDate}</td>
                          <td className="p-3 text-slate-500">{dayName}</td>
                          <td className="p-3">
                            {isEditing ? (
                              <Input
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEdit(date); if (e.key === 'Escape') setEditingDate(null); }}
                                className="h-9 max-w-xs"
                                autoFocus
                                data-testid={`edit-menu-input-${date}`}
                              />
                            ) : (
                              <>
                                {food}
                                {isToday && <Badge className="ml-2 bg-[#FFC107] text-slate-900 text-xs">Today</Badge>}
                              </>
                            )}
                          </td>
                          <td className="p-3 text-right whitespace-nowrap">
                            {isEditing ? (
                              <>
                                <Button size="sm" onClick={() => handleSaveEdit(date)} disabled={savingSchedule} className="h-8 bg-green-600 hover:bg-green-700 mr-1" data-testid={`save-menu-btn-${date}`}>
                                  <Save className="w-3.5 h-3.5" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingDate(null)} className="h-8" data-testid={`cancel-menu-btn-${date}`}>
                                  <X className="w-3.5 h-3.5" />
                                </Button>
                              </>
                            ) : (
                              <Button size="sm" variant="ghost" onClick={() => { setEditingDate(date); setEditValue(food); }} className="h-8 text-slate-500 hover:text-[#2B4F8C]" data-testid={`edit-menu-btn-${date}`}>
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Fixed Menu Items */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Regular Menu Items</CardTitle>
            <Button variant="outline" size="sm" onClick={handleAddAlternative}>
              <Plus className="w-4 h-4 mr-2" />
              Add Item
            </Button>
          </div>
          <p className="text-sm text-slate-500">These items are available every day</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {alternatives.map((alt, index) => (
            <div key={alt.id} id={`menu-item-${alt.id}`} className="border rounded-lg p-3 space-y-2 transition-all">
              <div className="flex items-center gap-3">
                <Input
                  value={alt.name}
                  onChange={(e) => {
                    const updated = [...alternatives];
                    updated[index].name = e.target.value;
                    setAlternatives(updated);
                  }}
                  placeholder="Menu item name..."
                  className="flex-1"
                />
                <Switch
                  checked={alt.is_active}
                  onCheckedChange={(checked) => {
                    const updated = [...alternatives];
                    updated[index].is_active = checked;
                    setAlternatives(updated);
                  }}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAlternatives(alternatives.filter((_, i) => i !== index))}
                  className="text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex items-center gap-4 pl-1">
                <span className="text-xs text-slate-400 font-medium">Available to:</span>
                {[
                  { key: 'skippers', label: 'Skippers (N)' },
                  { key: 'lower_mariners', label: 'Lower (B1-7, G1-7)' },
                  { key: 'upper_mariners', label: 'Upper (B8-11, G8-11)' },
                  { key: 'navigators', label: 'Navigators (B12-15, G12-15)' },
                ].map(div => {
                  const divisions = alt.divisions || [];
                  const checked = divisions.length === 0 || divisions.includes(div.key);
                  const allSelected = divisions.length === 0;
                  return (
                    <label key={div.key} className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const updated = [...alternatives];
                          let currentDivs = updated[index].divisions || [];
                          if (allSelected) {
                            // First click: switching from "all" to specific — select only what was just checked
                            currentDivs = e.target.checked 
                              ? ['skippers','lower_mariners','upper_mariners','navigators'].filter(d => d !== div.key ? true : e.target.checked)
                              : ['skippers','lower_mariners','upper_mariners','navigators'].filter(d => d !== div.key);
                          } else if (e.target.checked) {
                            currentDivs = [...currentDivs, div.key];
                          } else {
                            currentDivs = currentDivs.filter(d => d !== div.key);
                          }
                          // If all 4 selected, clear to empty (means "all")
                          if (currentDivs.length >= 4) currentDivs = [];
                          updated[index].divisions = currentDivs;
                          setAlternatives(updated);
                        }}
                        className="w-3.5 h-3.5 rounded accent-[#2B4F8C]"
                        data-testid={`div-${div.key}-${index}`}
                      />
                      <span className={`text-xs ${checked ? 'text-slate-700 font-medium' : 'text-slate-400'}`}>
                        {div.label}
                      </span>
                    </label>
                  );
                })}
                {(!alt.divisions || alt.divisions.length === 0) && (
                  <span className="text-xs text-green-600 font-medium ml-1">All groups</span>
                )}
              </div>
            </div>
          ))}
          <Button onClick={handleSaveAlternatives} disabled={saving} className="mt-4">
            <Save className="w-4 h-4 mr-2" />
            Save Menu
          </Button>
        </CardContent>
      </Card>

      {/* Lunch Periods */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Staff Lunch Menu</CardTitle>
            <Button variant="outline" size="sm" onClick={() => {
              setStaffMenuItems([...staffMenuItems, '']);
              toast.success('New staff item added — fill in the name below');
              setTimeout(() => {
                const container = document.getElementById('staff-menu-list');
                if (container) container.lastElementChild?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }, 100);
            }}>
              <Plus className="w-4 h-4 mr-2" />
              Add Item
            </Button>
          </div>
          <p className="text-sm text-slate-500">Menu items available to staff members</p>
        </CardHeader>
        <CardContent className="space-y-3" id="staff-menu-list">
          {staffMenuItems.map((item, index) => (
            <div key={index} className="flex items-center gap-3">
              <Input
                value={item}
                onChange={(e) => {
                  const updated = [...staffMenuItems];
                  updated[index] = e.target.value;
                  setStaffMenuItems(updated);
                }}
                placeholder="Staff menu item name..."
                className="flex-1"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStaffMenuItems(staffMenuItems.filter((_, i) => i !== index))}
                className="text-red-600"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          {staffMenuItems.length === 0 && (
            <p className="text-slate-400 text-sm text-center py-4">No staff menu items. Click "Add Item" to get started.</p>
          )}
          <Button 
            onClick={async () => {
              const valid = staffMenuItems.filter(i => i.trim());
              setSaving(true);
              try {
                await api.updateStaffMenu(valid);
                toast.success('Staff menu saved!');
              } catch { toast.error('Failed to save'); }
              finally { setSaving(false); }
            }} 
            disabled={saving} 
            className="mt-4"
            data-testid="save-staff-menu-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            Save Staff Menu
          </Button>
        </CardContent>
      </Card>

      {/* Lunch Periods */}
      <Card>
        <CardHeader>
          <CardTitle>Lunch Period Configuration</CardTitle>
          <p className="text-sm text-slate-500">Assign groups to each lunch period</p>
        </CardHeader>
        <CardContent className="space-y-6">
          {lunchPeriods.map((period, index) => (
            <div key={period.period_number} className="p-4 bg-slate-50 rounded-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold">{period.name}</h3>
                <Input
                  value={period.time}
                  onChange={(e) => {
                    const updated = [...lunchPeriods];
                    updated[index].time = e.target.value;
                    setLunchPeriods(updated);
                  }}
                  placeholder="e.g., 11:30 AM EST"
                  className="w-40"
                />
              </div>
              <div>
                <Label className="text-sm">Assigned Groups (comma-separated)</Label>
                <Input
                  value={period.groups?.join(', ') || ''}
                  onChange={(e) => {
                    const updated = [...lunchPeriods];
                    updated[index].groups = e.target.value.split(',').map(g => g.trim()).filter(Boolean);
                    setLunchPeriods(updated);
                  }}
                  placeholder="e.g., B01 Lobsters, B02 Sharks"
                  className="mt-2"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Available: {groups.slice(0, 5).map(g => g.name).join(', ')}...
                </p>
              </div>
            </div>
          ))}
          <Button onClick={handleSavePeriods} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            Save Periods
          </Button>
        </CardContent>
      </Card>

      {/* Yearly Season Reset */}
      <Card className="border-2 border-red-300 bg-red-50/30">
        <CardHeader>
          <CardTitle className="text-red-800 flex items-center gap-2">
            <RefreshCw className="w-5 h-5" />
            Yearly Season Reset
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-600">
            Archives all current season data (orders, trip orders, campers, staff, trips, menu schedule) 
            and resets the system for the next season. <strong>Lunch periods and fixed menu items are kept.</strong>
          </p>
          <p className="text-sm text-red-600 font-semibold">
            This action cannot be undone. All current data will be moved to archive storage.
          </p>
          <Button 
            variant="destructive"
            className="bg-red-700 hover:bg-red-800"
            data-testid="yearly-reset-btn"
            onClick={async () => {
              const password = window.prompt('Enter admin password to confirm yearly reset:');
              if (!password) return;
              if (!window.confirm('Are you absolutely sure? This will archive ALL current season data.')) return;
              try {
                const response = await apiClient.post('/api/admin/yearly-reset', { password });
                const d = response.data;
                toast.success(`Season ${d.year_archived} archived! ${Object.entries(d.archived).map(([k,v]) => `${k}: ${v}`).join(', ')}`);
                fetchConfig();
              } catch (error) {
                toast.error(error.response?.data?.detail || 'Reset failed');
              }
            }}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset for New Season
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// Trips Page
export const AdminTrips = () => {
  const [loading, setLoading] = useState(true);
  const [trips, setTrips] = useState([]);
  const [report, setReport] = useState([]);
  const [deleting, setDeleting] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tripsRes, reportRes] = await Promise.all([
        api.getTrips(),
        api.getTripOrdersReport()
      ]);
      setTrips(tripsRes.data || []);
      setReport(reportRes.data || []);
    } catch (error) {
      toast.error('Failed to load trips');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (tripId, tripName) => {
    if (!window.confirm(`Delete "${tripName}"? This will also remove all orders for this trip.`)) return;
    setDeleting(tripId);
    try {
      await api.deleteTrip(tripId);
      toast.success(`Deleted "${tripName}"`);
      fetchData();
    } catch { toast.error('Failed to delete trip'); }
    finally { setDeleting(null); }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const handlePrintTrip = async (trip) => {
    try {
      toast.info(`Generating PDF for ${trip.trip_name}...`);
      const response = await api.downloadTripReportPDF(trip.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `RRDC_Trip_${trip.trip_name.replace(/ /g, '_')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  const handlePrintAllTrips = async () => {
    try {
      toast.info('Generating all trip reports PDF...');
      const response = await api.downloadTripReportPDF();
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'RRDC_All_Trip_Reports.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  const [resetLoading, setResetLoading] = useState(false);

  const handleResetTripOrders = async () => {
    const password = window.prompt('Enter reset password:');
    if (!password) return;
    setResetLoading(true);
    try {
      const response = await apiClient.post('/api/trips/reset', { password });
      toast.success(`Reset complete — ${response.data.deleted} trip orders deleted`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid password');
    } finally {
      setResetLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#2B4F8C]" />
      </div>
    );
  }

  // Group trips by date
  const tripsByDate = {};
  trips.forEach(t => {
    if (!tripsByDate[t.date]) tripsByDate[t.date] = [];
    tripsByDate[t.date].push(t);
  });

  return (
    <div className="p-4 lg:p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-900">Trip Orders</h1>
          <p className="text-sm text-slate-500">{trips.length} trips &middot; {report.reduce((s, r) => s + r.total_orders, 0)} total orders</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={handleResetTripOrders} 
            variant="outline"
            className="border-red-300 text-red-600 hover:bg-red-50" 
            disabled={resetLoading}
            data-testid="reset-trip-orders-btn"
          >
            {resetLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Reset All Trip Orders
          </Button>
          {report.length > 0 && (
            <Button onClick={handlePrintAllTrips} className="bg-green-700 hover:bg-green-800 text-white" data-testid="download-all-trips-pdf-btn">
              <Download className="w-4 h-4 mr-2" />
              Download All Trip Reports PDF
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-6">
        {Object.entries(tripsByDate).map(([date, dateTrips]) => (
          <div key={date}>
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-[#2B4F8C]" />
              <h2 className="text-base font-bold text-slate-700">{formatDate(date)}</h2>
            </div>
            <div className="grid gap-2">
              {dateTrips.map(trip => {
                const tripReport = report.find(r => r.trip_id === trip.id);
                const orderCount = tripReport?.total_orders || 0;
                return (
                  <Card key={trip.id} className="hover:shadow-sm transition-shadow" data-testid={`admin-trip-${trip.id}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Bus className="w-5 h-5 text-[#2B4F8C]" />
                          <div>
                            <span className="font-bold text-slate-900">{trip.trip_name}</span>
                            <span className="text-sm text-slate-500 ml-2">{mergeGroupDisplay(trip.groups).join(', ')}</span>
                          </div>
                          {orderCount > 0 && (
                            <Badge className="bg-green-100 text-green-800">{orderCount} orders</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {orderCount > 0 && (
                            <Button variant="outline" size="sm" onClick={() => handlePrintTrip(trip)} data-testid={`print-trip-${trip.id}`}>
                              <Printer className="w-4 h-4 mr-1" /> PDF
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600"
                            disabled={deleting === trip.id}
                            onClick={() => handleDelete(trip.id, trip.trip_name)}
                            data-testid={`delete-trip-${trip.id}`}
                          >
                            {deleting === trip.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                      {/* Trip food totals + group breakdown with camper details */}
                      {tripReport && tripReport.food_totals && (
                        <div className="mt-3 border-t pt-3">
                          {/* Trip-level food totals */}
                          <div className="flex flex-wrap gap-1 mb-3">
                            {Object.entries(tripReport.food_totals)
                              .sort((a, b) => b[1] - a[1])
                              .map(([food, count]) => (
                                <Badge key={food} variant="outline" className="text-xs bg-blue-50 text-[#2B4F8C] border-blue-200">
                                  {food}: {count}
                                </Badge>
                              ))}
                          </div>
                          {/* Group-level breakdown with camper list */}
                          {tripReport.groups_data?.map((gd) => (
                            <div key={gd.group_name} className="mb-4 border rounded-lg overflow-hidden" data-testid={`trip-group-${gd.group_name}`}>
                              {/* Group header */}
                              <div className="bg-slate-100 px-3 py-2 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-bold text-slate-800">{gd.group_name}</span>
                                  <Badge variant="secondary" className="text-xs">{gd.total_orders} orders</Badge>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {Object.entries(gd.food_totals).sort((a, b) => b[1] - a[1]).map(([f, c]) => (
                                    <span key={f} className="text-xs text-slate-500">{f}: {c}</span>
                                  ))}
                                </div>
                              </div>
                              {/* Camper order table */}
                              <table className="w-full text-xs" data-testid={`trip-group-table-${gd.group_name}`}>
                                <thead>
                                  <tr className="bg-slate-50 border-b">
                                    <th className="text-left px-3 py-1.5 font-semibold text-slate-600">Camper</th>
                                    <th className="text-left px-3 py-1.5 font-semibold text-slate-600">Lunch Choice</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {gd.orders?.map((order, idx) => (
                                    <tr key={order.camper_id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                      <td className="px-3 py-1.5 text-slate-700">{order.camper_name}</td>
                                      <td className="px-3 py-1.5 text-slate-700">{order.lunch_choice}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}

        {trips.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <Bus className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="text-lg font-medium">No trips</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Sync Page
export const AdminSync = () => {
  const [loading, setLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState(null);
  const [cronStatus, setCronStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncingStaff, setSyncingStaff] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [staffCount, setStaffCount] = useState(0);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const [syncRes, cronRes] = await Promise.all([
        api.getSyncStatus(),
        api.getCronStatus()
      ]);
      setSyncStatus(syncRes.data);
      setCronStatus(cronRes.data);
      setStaffCount(syncRes.data?.total_staff || 0);
    } catch (error) {
      toast.error('Failed to load sync status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.triggerSync();
      toast.success('Camper sync started!');
      setTimeout(fetchStatus, 5000);
    } catch (error) {
      toast.error('Failed to start sync');
    } finally {
      setSyncing(false);
    }
  };

  const handleStaffSync = async () => {
    setSyncingStaff(true);
    try {
      await api.syncStaff();
      toast.success('Staff sync started!');
      setTimeout(fetchStatus, 5000);
    } catch (error) {
      toast.error('Failed to start staff sync');
    } finally {
      setSyncingStaff(false);
    }
  };

  const handleDailyReset = async () => {
    if (!window.confirm('Are you sure? This will archive all orders, reset food of the day, and re-sync rosters.')) return;
    setResetting(true);
    try {
      const res = await api.resetDaily();
      toast.success(`Daily reset complete! Archived ${res.data.camper_orders_archived || 0} camper + ${res.data.staff_orders_archived || 0} staff orders.`);
      setTimeout(fetchStatus, 3000);
    } catch (error) {
      toast.error('Failed to run daily reset');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#2B4F8C]" />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-black text-slate-900">CampMinder Sync</h1>
        <div className="flex items-center gap-2">
          <Button onClick={handleStaffSync} disabled={syncingStaff} variant="outline" data-testid="sync-staff-btn">
            {syncingStaff ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Users className="w-4 h-4 mr-2" />}
            Sync Staff
          </Button>
          <Button onClick={handleSync} disabled={syncing} data-testid="sync-campers-btn">
            {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Sync Campers
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-sm text-slate-600">Last Sync</p>
            <p className="text-lg font-bold">{syncStatus?.last_sync || 'Never'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-sm text-slate-600">Total Campers</p>
            <p className="text-3xl font-bold text-[#2B4F8C]">{syncStatus?.total_campers || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-sm text-slate-600">Total Groups</p>
            <p className="text-3xl font-bold text-[#2B4F8C]">{syncStatus?.total_groups || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-sm text-slate-600">Staff Synced</p>
            <p className="text-3xl font-bold text-[#2B4F8C]">{staffCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-sm text-slate-600">Status</p>
            <Badge className={syncStatus?.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
              {syncStatus?.status || 'Unknown'}
            </Badge>
          </CardContent>
        </Card>
      </div>

      {/* Daily Reset Cron Job */}
      <Card className="border-2 border-slate-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#2B4F8C]" />
                Daily Midnight Reset
              </CardTitle>
              <p className="text-sm text-slate-500 mt-1">
                Automatically archives orders, clears current data, and re-syncs rosters at 12:00 AM EST
              </p>
            </div>
            <Button onClick={handleDailyReset} disabled={resetting} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" data-testid="manual-reset-btn">
              {resetting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Run Now
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Cron Status</p>
              <Badge className={
                cronStatus?.cron_status === 'running' ? 'bg-green-100 text-green-800' :
                cronStatus?.cron_status === 'executing' ? 'bg-blue-100 text-blue-800' :
                cronStatus?.cron_status === 'error' ? 'bg-red-100 text-red-800' :
                'bg-slate-100 text-slate-800'
              } data-testid="cron-status-badge">
                {cronStatus?.cron_status || 'Unknown'}
              </Badge>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Schedule</p>
              <p className="text-sm font-semibold">{cronStatus?.schedule || 'Daily at 12:00 AM EST'}</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Last Run</p>
              <p className="text-sm font-semibold">{cronStatus?.last_run || 'Never'}</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Next Run</p>
              <p className="text-sm font-semibold">{cronStatus?.next_run || 'Pending...'}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
